/**
 * Character schema migrations. A record read from IndexedDB or an import file may carry an older
 * `schema`; each step below lifts it by one. Steps are pure and never touch the original object.
 */
import type { Character } from "./model";
import { CHARACTER_SCHEMA } from "./model";

type Step = (raw: Record<string, unknown>) => Record<string, unknown>;

/** Index n lifts schema n to n+1. Schema 1 is the first shipped. */
const STEPS: Record<number, Step> = {
  // Schema 2 (#8): the Character keeps its RollOption toggle states.
  1: (raw) => ({ ...raw, toggles: typeof raw.toggles === "object" && raw.toggles !== null && !Array.isArray(raw.toggles) ? raw.toggles : {} }),
  // Schema 3 (#18): the Session slot holds a real Session. No build before this wrote one, so any earlier value is dropped.
  2: (raw) => ({ ...raw, session: null }),
  // Schema 4 (#10): the user's spells per Spellcasting Entry and hand-declared Entries.
  3: (raw) => ({ ...raw, spellcasting: { entries: {}, manual: [] } }),
  // Schema 5 (#11): the inventory and coins.
  4: (raw) => ({ ...raw, inventory: { items: [], coins: {}, counter: 1 } }),
  // Schema 6 (#13): the Familiar's name, attribute and Overrides.
  5: (raw) => ({ ...raw, familiar: null }),
  // Schema 7 (#17): the record of every Snapshot migration the Character went through.
  6: (raw) => ({ ...raw, migrations: [] }),
  // Schema 8 (#20): the Roll Log beside the Session. No build before this kept one.
  7: (raw) => ({ ...raw, rollLog: { entries: [], dropped: 0, counter: 1 } }),
  // Schema 9 (#22): immunities, weaknesses and resistances entered by hand.
  8: (raw) => ({ ...raw, iwr: [] }),
};

export class MigrationError extends Error {
  override readonly name = "MigrationError";
}

/**
 * Lift a raw record to the current CHARACTER_SCHEMA. Does not validate; call `validateCharacter`
 * on the result. Returns the input untouched (same reference) when already current.
 */
export function migrateCharacter(raw: unknown): unknown {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return raw;
  let rec = raw as Record<string, unknown>;
  const schema = rec.schema;
  if (typeof schema !== "number" || !Number.isInteger(schema) || schema < 1) return raw;
  if (schema > CHARACTER_SCHEMA) {
    throw new MigrationError(`Character schema ${schema} is newer than this build understands (${CHARACTER_SCHEMA}); update the app`);
  }
  for (let v = schema; v < CHARACTER_SCHEMA; v++) {
    const step = STEPS[v];
    if (!step) throw new MigrationError(`no migration from Character schema ${v}`);
    rec = { ...step(rec), schema: v + 1 };
  }
  return rec as unknown as Character;
}
